import { Dispatch } from 'redux';
import axios, { AxiosResponse } from 'axios';
import {
    DispatchFunction,
    IGroup,
    IGroupMemberCount,
    IAgentDetail,
    IActiveFilter,
    ICustomSelect,
    IGroupListCount,
    IPeopleListCount,
    IVaultPasswordUrl
} from '@app/models';
import {
    GET_PEOPLE_COUNT_IN_GROUP,
    GROUPS_COUNT,
    GET_GROUPS_COUNT_IN_GROUP,
    GET_GROUP,
    GET_ALL_GROUPS,
    GET_GROUPS_MEMBER_COUNT,
    GET_GROUPS_VAULT_COUNT,
    GET_GROUPS_APPLICATION_COUNT,
    GET_GROUPS_AGENT_DETAIL,
    GROUPS_LOADING,
    GET_GROUPS_DTO,
    GET_APPLICATIONS_IN_GROUP,
    GET_VAULTS_IN_GROUP,
    GET_USERS_TO_GROUPS,
    GET_GROUPS_TO_GROUPS,
    GROUPS_FOR_SELECT,
    GET_GROUPS_IN_GROUP_COUNT,
    GET_USERS_IN_GROUP_COUNT
} from '@app/store/constants';
import { groupsService } from '@app/services';
import { loadingData } from './basic.actions';
import { getModalSwitch, getLoaderBtn, getConfirmModalSwitch } from '.';
import { toast } from 'react-toastify';

export function handleGetGroupsData(
    orderBy: string,
    asc: string,
    top: number,
    skip: number,
    organizationId: string,
    isReset: boolean,
    filters?: IActiveFilter[]
): DispatchFunction {
    return (dispatch: Dispatch) => {
        dispatch(loadingData(true));
        if (isReset) {
            dispatch(groupsCount(0));
            dispatch(getAllGroups([]));
            dispatch(getGroupMembersCount([]));
            dispatch(getGroupVaultsCount([]));
            dispatch(getGroupApplicationsCount([]));
        }

        groupsService.getGroups(orderBy, asc, top, skip, organizationId, isReset, filters).then((response: AxiosResponse) => {
            const groups = response.data.value;
            const groupIds = getCountSetup(groups);

            if (isReset) {
                dispatch(groupsCount(response.data['@odata.count']));
            }

            dispatch(getAllGroups(response.data.value));

            if (response.data.value.length > 0) {
                axios
                    .all([
                        groupsService.getGroupMemberCount(groupIds),
                        groupsService.getGroupVaultCount(groupIds),
                        groupsService.getGroupApplicationCount(groupIds)
                    ])
                    .then(
                        axios.spread((membersCount, vaultsCount, applicationsCount, groupListCount, peopleListCount) => {
                            dispatch(getGroupMembersCount(membersCount.data.value));
                            dispatch(getGroupVaultsCount(vaultsCount.data.value));
                            dispatch(getGroupApplicationsCount(applicationsCount.data.value));
                        })
                    )
                    .then(() => {
                        dispatch(loadingData(false));
                    })
                    .catch(() => {
                        // TODO: Uncomment when we will set up dev variable
                        // console.log(error);
                        dispatch(loadingData(false));
                    });
            } else {
                dispatch(loadingData(false));
            }
        });
    };

    function getCountSetup(groups: IGroup[]) {
        let groupIds: string[] = [];
        groups.forEach((element: IGroup) => {
            groupIds.push(element.Id);
        });

        return groupIds;
    }
}

export function handleGroupsForSelect(top: number, skip: number, isReset: boolean, filters?: IActiveFilter[]): DispatchFunction {
    return (dispatch: Dispatch) => {
        groupsService.getGroupsForPolicies(top, skip, isReset, filters).then((response: AxiosResponse) => {
            const groups: IGroup[] = response.data.value;
            const selectGroups: ICustomSelect[] = [];
            groups.forEach((group: IGroup) => {
                selectGroups.push({
                    id: group.Id,
                    name: group.Name,
                    organizationId: group.OrganizationId
                });
            });
            dispatch(groupsForSelect(selectGroups));
        });
    };
}

export function handleAllGroupsForSelect(top: number): DispatchFunction {
    let values: IVaultPasswordUrl[] = [];
    let count = 0;

    const getAllGroupsForSelect = (): Promise<any> => {
        return groupsService.getGroupsForPolicies(top, count, true).then((resp: AxiosResponse) => {
            count += resp.data.value.length;
            values = [...values, ...resp.data.value];

            if (count < resp.data['@odata.count']) {
                return getAllGroupsForSelect();
            } else {
                return values;
            }
        });
    };

    return (dispatch: Dispatch) => {
        getAllGroupsForSelect().then((values: any) => {
            const groups: IGroup[] = values;
            const selectGroups: ICustomSelect[] = [];
            groups.forEach((group: IGroup) => {
                selectGroups.push({
                    id: group.Id,
                    name: group.Name,
                    organizationId: group.OrganizationId
                });
            });
            dispatch(groupsForSelect(selectGroups));
        });
    };
}

export function handleGetGroupsDTO(orderBy: string, asc: string, organizationId: string): DispatchFunction {
    return (dispatch: Dispatch) => {
        groupsService.getGroupsDTO(orderBy, asc, organizationId).then((response: AxiosResponse) => {
            dispatch(getGroupsDTO(response.data.value));
        });
    };
}

export function handleGetGroupData(groupId: string): DispatchFunction {
    return (dispatch: Dispatch) => {
        dispatch(loadingData(true));
        groupsService
            .getGroup(groupId)
            .then((response: AxiosResponse) => {
                dispatch(getGroup(response.data));
            })
            .then(() => dispatch(loadingData(false)));
    };
}

export function handleGetApplicationsInGroupData(groupId: string): DispatchFunction {
    return (dispatch: Dispatch) => {
        groupsService.getApplicationsInGroup(groupId).then((response: AxiosResponse) => {
            dispatch(getApplicationsInGroup(response.data.value));
        });
    };
}

export function handleGetVaultsInGroupData(groupId: string): DispatchFunction {
    return (dispatch: Dispatch) => {
        groupsService.getVaultsInGroup(groupId).then((response: AxiosResponse) => {
            dispatch(getVaultsInGroup(response.data.value));
        });
    };
}

export function handleGetPeopleCountInGroup(groupId: string): DispatchFunction {
    return (dispatch: Dispatch) => {
        groupsService.getPeopleCountInGroup(groupId).then((response: AxiosResponse) => {
            dispatch(getPeopleCountInGroup(response.data.value));
        });
    };
}

export function handleGetGroupsCountInGroup(groupId: string): DispatchFunction {
    return (dispatch: Dispatch) => {
        groupsService.getGroupsCountInGroup(groupId).then((response: AxiosResponse) => {
            dispatch(getGroupsCountInGroup(response.data.value));
        });
    };
}

export function handleAddUsersToGroups(groupId: string, groupMembership: any[]): DispatchFunction {
    return (dispatch: Dispatch) => {
        groupsService
            .addUserForGroup(groupId, groupMembership)
            .then((response: AxiosResponse) => {
                dispatch(addUsersToGroups(response.data));
            })
            .then(() => {
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
                dispatch(getConfirmModalSwitch(true));
            })
            .then(() => {
                groupsService
                    .getPeopleCountInGroup(groupId)
                    .then((response: AxiosResponse) => {
                        dispatch(getPeopleCountInGroup(response.data.value));
                    })
                    .then(() => dispatch(loadingData(false)));
            })
            .catch((error) => toast.error(error));
    };
}

export function handleAddGroupsToGroups(groupId: string, groupMembership: any[]): DispatchFunction {
    return (dispatch: Dispatch) => {
        groupsService
            .addGroupForGroup(groupId, groupMembership)
            .then((response: AxiosResponse) => {
                dispatch(addGroupsToGroups(response.data));
            })
            .then(() => {
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
                dispatch(getConfirmModalSwitch(true));
            })
            .then(() => {
                groupsService
                    .getGroupsCountInGroup(groupId)
                    .then((response: AxiosResponse) => {
                        dispatch(getGroupsCountInGroup(response.data.value));
                    })
                    .then(() => dispatch(loadingData(false)));
            })
            .catch((error) => toast.error(error));
    };
}

export function handleRemoveGroupsFromGroup(groupId: string, groupMembership: any[]): DispatchFunction {
    return (dispatch: Dispatch) => {
        groupsService
            .removeGroupsFromGroup(groupId, groupMembership)
            .then((response: AxiosResponse) => {
                dispatch(addGroupsToGroups(response.data));
            })
            .then(() => {
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
                dispatch(getConfirmModalSwitch(true));
            })
            .then(() => {
                groupsService
                    .getGroupsCountInGroup(groupId)
                    .then((response: AxiosResponse) => {
                        dispatch(getGroupsCountInGroup(response.data.value));
                    })
                    .then(() => dispatch(loadingData(false)));
            })
            .catch((error) => toast.error(error));
    };
}

export function handleRemoveUsersFromGroup(groupId: string, groupMembership: any[]): DispatchFunction {
    return (dispatch: Dispatch) => {
        groupsService
            .removeUsersFromGroup(groupId, groupMembership)
            .then((response: AxiosResponse) => {
                dispatch(addUsersToGroups(response.data));
            })
            .then(() => {
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
                dispatch(getConfirmModalSwitch(true));
            })
            .then(() => {
                groupsService
                    .getPeopleCountInGroup(groupId)
                    .then((response: AxiosResponse) => {
                        dispatch(getPeopleCountInGroup(response.data.value));
                    })
                    .then(() => dispatch(loadingData(false)));
            })
            .catch((error) => toast.error(error));
    };
}

export function handleDisconnectGroupFromDirsync(groupId: string, handleError: Function, handleSuccess: Function) {
    groupsService.disconnectGroupFromDirsync(groupId, handleError, handleSuccess);
}

export function getAllGroups(groups: IGroup[]) {
    return {
        payload: groups,
        type: GET_ALL_GROUPS
    };
}

export function getGroupsDTO(groups: IGroup[]) {
    return {
        payload: groups,
        type: GET_GROUPS_DTO
    };
}

export function getGroupMembersCount(groupMemberCount: IGroupMemberCount[]) {
    return {
        payload: groupMemberCount,
        type: GET_GROUPS_MEMBER_COUNT
    };
}

export function getGroupListCount(groupListCount: IGroupListCount[]) {
    return {
        payload: groupListCount,
        type: GET_GROUPS_IN_GROUP_COUNT
    };
}

export function getPeopleListCount(peopleListCount: IPeopleListCount[]) {
    return {
        payload: peopleListCount,
        type: GET_USERS_IN_GROUP_COUNT
    };
}

export function getGroupVaultsCount(groupVaultCount: IGroupMemberCount[]) {
    return {
        payload: groupVaultCount,
        type: GET_GROUPS_VAULT_COUNT
    };
}

export function getGroupApplicationsCount(groupApplicationCount: IGroupMemberCount[]) {
    return {
        payload: groupApplicationCount,
        type: GET_GROUPS_APPLICATION_COUNT
    };
}

export function getGroupAgentDetails(agentDetail: IAgentDetail[]) {
    return {
        payload: agentDetail,
        type: GET_GROUPS_AGENT_DETAIL
    };
}

export function groupsLoading(loading: boolean) {
    return {
        payload: loading,
        type: GROUPS_LOADING
    };
}

export function getGroup(groupData: IGroup) {
    return {
        payload: groupData,
        type: GET_GROUP
    };
}

export function getApplicationsInGroup(applicationsInGroupData: any) {
    return {
        payload: applicationsInGroupData,
        type: GET_APPLICATIONS_IN_GROUP
    };
}

export function getVaultsInGroup(vaultsInGroupData: any) {
    return {
        payload: vaultsInGroupData,
        type: GET_VAULTS_IN_GROUP
    };
}

export function getPeopleCountInGroup(peopleCount: number) {
    return {
        payload: peopleCount,
        type: GET_PEOPLE_COUNT_IN_GROUP
    };
}

export function getGroupsCountInGroup(peopleCount: number) {
    return {
        payload: peopleCount,
        type: GET_GROUPS_COUNT_IN_GROUP
    };
}

export function groupsCount(count: number) {
    return {
        payload: count,
        type: GROUPS_COUNT
    };
}

export function addUsersToGroups(usersToGroups: any[]) {
    return {
        payload: usersToGroups,
        type: GET_USERS_TO_GROUPS
    };
}

export function addGroupsToGroups(groupsToGroups: any[]) {
    return {
        payload: groupsToGroups,
        type: GET_GROUPS_TO_GROUPS
    };
}

export function groupsForSelect(groups: ICustomSelect[]) {
    return {
        payload: groups,
        type: GROUPS_FOR_SELECT
    };
}
